// ** React Imports
import { useEffect, useState } from 'react';

import { client } from 'src/utils/axios-client';
import { LeadScreenStore, LeadActivity, Lead, NextActivity, LeadActivityStore, ViewType } from 'src/stores/LeadStore';
import { leadEndpoints } from '../../../configs/endpoints';

// ** MUI Imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import { styled } from '@mui/material/styles';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import MuiTimeline, { TimelineProps } from '@mui/lab/Timeline';
import { timeSince } from 'src/utils/date-utils';
import Icon from 'src/features/common/CustomIcon';
import { ActivityColor } from 'src/utils/date-utils';
import Tooltip from '@mui/material/Tooltip';
import { previousDateDays } from 'src/utils/date-utils';
import Grid from '@mui/material/Grid';
import QuestionAnswerTwoToneIcon from '@mui/icons-material/QuestionAnswerTwoTone';
import Link from '@mui/material/Link';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import MoreHorizTwoToneIcon from '@mui/icons-material/MoreHorizTwoTone';
import IconButton from '@mui/material/IconButton';

import { nextDateDays } from 'src/utils/date-utils';
import ActivityDetails from './ActivityDetails';
import { ActivityScreenStore } from 'src/stores/LeadStore';
import EditActivityView from './EditActivityView';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import _ from 'lodash';
import { ActivityReady } from 'src/utils/date-utils';
import StandardSkeleton from '../../common/StandardSkeleton';
import SectionAppBar from '../../../v2/components/Section/SectionAppBar';
import CloseButton from '../../../v2/components/Button/CloseButton';

// Styled Timeline component
const Timeline = styled(MuiTimeline)<TimelineProps>(({ theme }) => ({
    margin: 0,
    padding: 0,
    marginLeft: theme.spacing(0.75),
    '& .MuiTimelineItem-root': {
        '&:before': {
            display: 'none',
        },
        '&:last-child': {
            minHeight: 60,
        },
    },
}));

type Props = {
    onClose?: () => void;
};

const ActivityTimelineView = ({ onClose }: Props) => {
    const records = LeadActivityStore.useState((s) => s.items);
    const selectedLead = LeadScreenStore.useState((s) => s.selectedLead);
    const [selectedActivity, setSelectedActivity] = useState(null as null | LeadActivity);
    const [deletedActivity, setSDeletedActivity] = useState(null as null | LeadActivity);
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        const fetchActivities = async () => {
            setHasLoaded(false);

            const response = await client.get(leadEndpoints.getActivities, { params: { id: selectedLead?.id } });
            LeadActivityStore.update((s) => {
                s.items = response.data;
            });
            setHasLoaded(true);
        };

        if (selectedLead) fetchActivities();
        if (!selectedLead)
            LeadActivityStore.update((s) => {
                s.items = [];
            });
    }, [selectedLead]);

    const nextActivity: NextActivity = {
        hasRecord: selectedLead?.nextActivity != null ? true : false,
        name: selectedLead?.nextActivity != null ? selectedLead?.nextActivity.name : '',
        date: selectedLead?.nextActivityDateTime != null ? selectedLead?.nextActivityDateTime : '',
        icon: selectedLead?.nextActivity != null ? selectedLead?.nextActivity.icon : 'accessibility_new',
        owner: selectedLead?.nextActivityOwner != null ? selectedLead?.nextActivityOwner.fullName : '',
    };

    const handleDeleteConfirm = async (data: LeadActivity) => {
        await client.post(leadEndpoints.deleteActivityEndpoint, data).then(async (response) => {
            setSDeletedActivity(null);
            LeadActivityStore.update((s) => {
                const list = _.cloneDeep(s.items);
                const activityIndex = list.findIndex((s) => s.id == data.id);
                list.splice(activityIndex, 1);
                s.items = list;
            });
        });
    };

    const onExecuteActivity = () => {
        LeadScreenStore.update((s) => {
            s.selectedLead = selectedLead;
            s.selectedView = ViewType.DoActivity;
            s.editMode = false;
        });
    };

    if (hasLoaded) {
        return (
            <>
                <SectionAppBar
                    title={'Activity Timeline'}
                    buttons={
                        <>
                            <CloseButton onClick={onClose}>Close</CloseButton>
                        </>
                    }
                    subTitle=""
                />
                <Card sx={{ mb: 6 }} data-testid="activity-timeline">
                    <CardContent>
                        <Timeline>
                            {nextActivity.hasRecord && (
                                <TimelineItem data-testid="timeline-item">
                                    <TimelineSeparator>
                                        <Tooltip title={nextActivity.name}>
                                            <TimelineDot>
                                                <Icon
                                                    icon={nextActivity.icon}
                                                    sx={{ color: 'blue', fontSize: 20 }}
                                                ></Icon>
                                            </TimelineDot>
                                        </Tooltip>
                                        <TimelineConnector />
                                    </TimelineSeparator>
                                    <TimelineContent>
                                        <Typography variant="body2" sx={{ mr: 1 }}>
                                            Scheduled Activity: {nextActivity.name}
                                        </Typography>
                                        {
                                            <Typography variant="caption">
                                                Due {nextDateDays(nextActivity.date)} by {nextActivity.owner}
                                            </Typography>
                                        }
                                        {nextActivity &&
                                            nextActivity.name != '' &&
                                            nextActivity.date &&
                                            ActivityReady(nextActivity.date) < 1 && (
                                                <Tooltip title={'Activity due:' + nextActivity.name}>
                                                    <IconButton aria-label="Next activity" onClick={onExecuteActivity}>
                                                        <Icon
                                                            icon={nextActivity.icon}
                                                            sx={{ color: ActivityColor(nextActivity.date) }}
                                                        ></Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        {nextActivity &&
                                            nextActivity.name != '' &&
                                            nextActivity.date &&
                                            ActivityReady(nextActivity.date) > 0 && (
                                                <Tooltip title={'Activity due:' + nextActivity.name}>
                                                    <IconButton aria-label="Next activity" disabled>
                                                        <Icon
                                                            icon={nextActivity.icon}
                                                            sx={{ color: ActivityColor(nextActivity.date) }}
                                                        ></Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                    </TimelineContent>
                                </TimelineItem>
                            )}

                            {records.map((record) => (
                                <TimelineItem key={record.id} data-testid="timeline-item">
                                    {!record.isAction && (
                                        <TimelineSeparator>
                                            <Tooltip title={record.currentActivity.name}>
                                                <TimelineDot variant="outlined">
                                                    <Icon
                                                        icon={record.currentActivity.icon}
                                                        sx={{ color: 'CornflowerBlue', fontSize: 20 }}
                                                    ></Icon>
                                                </TimelineDot>
                                            </Tooltip>
                                            <TimelineConnector />
                                        </TimelineSeparator>
                                    )}
                                    {record.isAction && (
                                        <TimelineSeparator>
                                            <Tooltip title={record.actionActivityName}>
                                                <TimelineDot variant="outlined">
                                                    <Icon
                                                        icon={'assistant_photo_two_tone'}
                                                        sx={{ color: 'CadetBlue', fontSize: 20 }}
                                                    ></Icon>
                                                </TimelineDot>
                                            </Tooltip>
                                            <TimelineConnector />
                                        </TimelineSeparator>
                                    )}
                                    {!record.isAction && (
                                        <TimelineContent>
                                            <Typography variant="body2" sx={{ mr: 1 }}>
                                                <Link
                                                    underline="hover"
                                                    component="button"
                                                    variant="body2"
                                                    onClick={() => setSelectedActivity(record)}
                                                >
                                                    {record.currentActivityOutcome.name}
                                                </Link>
                                                {record.currentActivityOutcome &&
                                                    record.currentActivityOutcome.isMeaningfull && (
                                                        <Tooltip title="Meaningful Interaction">
                                                            <QuestionAnswerTwoToneIcon
                                                                sx={{ color: 'LightSeaGreen' }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                            </Typography>
                                            <Typography variant="caption">
                                                {previousDateDays(record.currentActivityDate)} by{' '}
                                                {record.currentActivityOwnerName}
                                            </Typography>
                                            <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                                <Tooltip title="Details">
                                                    <IconButton
                                                        aria-label="Details"
                                                        onClick={() => setSelectedActivity(record)}
                                                    >
                                                        <MoreHorizTwoToneIcon color="info" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit Activity Outcome">
                                                    <IconButton
                                                        aria-label="Edit Activity Outcome"
                                                        onClick={() =>
                                                            ActivityScreenStore.update((s) => {
                                                                s.selectedActivity = record;
                                                                s.openActivity = true;
                                                            })
                                                        }
                                                    >
                                                        <EditTwoToneIcon color="info" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Activity">
                                                    <IconButton
                                                        aria-label="Delete Activityy"
                                                        onClick={() => setSDeletedActivity(record)}
                                                    >
                                                        <DeleteForeverTwoToneIcon color="info" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TimelineContent>
                                    )}
                                    {record.isAction && (
                                        <TimelineContent>
                                            <Typography variant="body2" sx={{ mr: 1 }}>
                                                <Link
                                                    underline="hover"
                                                    component="button"
                                                    variant="body2"
                                                    onClick={() => setSelectedActivity(record)}
                                                >
                                                    {record.actionActivityName}
                                                </Link>
                                            </Typography>
                                            <Typography variant="caption">
                                                {previousDateDays(record.currentActivityDate)} by{' '}
                                                {record.currentActivityOwnerName}
                                            </Typography>
                                            <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                                                <Tooltip title="Details">
                                                    <IconButton
                                                        aria-label="Details"
                                                        onClick={() => setSelectedActivity(record)}
                                                    >
                                                        <MoreHorizTwoToneIcon color="info" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TimelineContent>
                                    )}
                                </TimelineItem>
                            ))}
                        </Timeline>
                        {selectedActivity && (
                            <ActivityDetails
                                open={selectedActivity != null}
                                onClose={() => setSelectedActivity(null)}
                                activity={selectedActivity}
                            />
                        )}
                        <EditActivityView />
                        {deletedActivity && (
                            <ConfirmationDialog
                                open={deletedActivity != null}
                                title="Delete Actvity"
                                message="Are you sure you want to delete this historical activity?"
                                onCancel={() => setSDeletedActivity(null)}
                                onConfirm={() => handleDeleteConfirm(deletedActivity)}
                                confirmButtonText="Confirm"
                                cancelButtonText="Cancel"
                            />
                        )}
                    </CardContent>
                </Card>
            </>
        );
    } else {
        return <StandardSkeleton />;
    }
};

export { ActivityTimelineView };

export default ActivityTimelineView;
